import React from "react"
import moment from "moment"

import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import {Body, H2, H4} from "@irati/ui/src/Typography"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import mainTheme from "@irati/ui/src/global/theme"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import {ZuoraCurrentPlan, RenewalQuoteStatus} from "@irati/types/ops"
import Card from "@irati/ui/src/Layout/Card"
import {RenewalPlanSubSource} from "@irati-tracking/admin/admin"

import RenewalHeader, {AddOns} from "../../Renewal/Header"
import FilterChip from "../../FilterChip"
import {GradientBody} from "../../RenewalCard/styled"
import {RowInfo, CustomCard} from "./styled"

interface Props extends InjectIntl {
  renewalStatus?: RenewalQuoteStatus
  currentPlan?: ZuoraCurrentPlan
  isManual: boolean
  addOns: AddOns[]
  onChangeModal: () => void
  onCallback: (subSource: RenewalPlanSubSource) => void
  isRenewalWindowOpen?: boolean
}

const CurrentPlanInfo: React.FC<Props> = ({
  currentPlan,
  addOns,
  renewalStatus,
  isManual,
  onChangeModal,
  onCallback,
  isRenewalWindowOpen,
  formatMessage
}) => {
  const isRenewalAvailable = renewalStatus === RenewalQuoteStatus.Available
  const isRenewalPending = renewalStatus === RenewalQuoteStatus.SentForSignature
  const isRenewalCompleted = renewalStatus === RenewalQuoteStatus.SignedInProgress || renewalStatus === RenewalQuoteStatus.Completed
  const isRenewalCancelled = renewalStatus === RenewalQuoteStatus.CancellationCreated

  const showBanner = isRenewalWindowOpen && (isRenewalAvailable || isRenewalPending || isRenewalCompleted || isRenewalCancelled)
  return (
    <>
      {showBanner && (
        <Card bgColor={mainTheme.colors.greys.lightest} padding={[3, 2]} marginBottom={4} noBorder>
          <Flex justifyContent="space-between">
            <Flex alignItems="center">
              <Icon icon={IconTypes.info} color={mainTheme.colors.tertiary.o_400} size={24} />
              <Body marginLeft color={mainTheme.colors.filterChip}>
                {isRenewalAvailable && <FormattedMessage id="admin.billing.currentPlan.renewal.title" />}
                {isRenewalPending &&
                  (!isManual ? (
                    <FormattedMessage id="admin.billing.currentPlan.pending.title" />
                  ) : (
                    <FormattedMessage id="admin.billing.currentPlan.isManual.title" />
                  ))}
                {isRenewalCompleted && <FormattedMessage id="admin.billing.currentPlan.complete.title" />}
                {isRenewalCancelled && <FormattedMessage id="admin.billing.currentPlan.cancellation.title" />}
              </Body>
            </Flex>
            {!isRenewalCancelled && (
              <Button onClick={() => onCallback(RenewalPlanSubSource.renewPlanBannerCTA)}>
                {isRenewalAvailable && <FormattedMessage id="admin.billing.currentPlan.renewal.btn" />}
                {isRenewalPending && <FormattedMessage id="admin.billing.currentPlan.pending.btn" />}
                {isRenewalCompleted && <FormattedMessage id="admin.billing.currentPlan.complete.btn" />}
              </Button>
            )}
          </Flex>
        </Card>
      )}
      <Card bgColor={mainTheme.colors.greys.lightest} padding={4} noBorder>
        <Flex justifyContent="space-between" width="100%" marginBottom={2}>
          <H2>
            <FormattedMessage id="admin.billing.currentPlan.title" />
          </H2>
          {showBanner && (
            <GradientBody small padding={0.5} marginRight={2}>
              {isRenewalAvailable && <FormattedMessage id="admin.billing.currentPlan.renewalAvailable" />}
              {isRenewalPending &&
                (!isManual ? (
                  <FormattedMessage id="admin.billing.currentPlan.renewalSubmitted" />
                ) : (
                  <FormattedMessage id="admin.billing.currentPlan.renewalIsManual" />
                ))}
              {isRenewalCancelled && <FormattedMessage id="admin.billing.currentPlan.cancellation.pending" />}
              {isRenewalCompleted && <FormattedMessage id="admin.billing.currentPlan.renewalIsCompleted" />}
            </GradientBody>
          )}
        </Flex>
        {addOns && (
          <Flex gap={mainTheme.spacing.one} marginBottom={4}>
            {addOns.map((data, i) => (
              <FilterChip
                key={i}
                bgColor={mainTheme.colors.primary2}
                textColor={mainTheme.colors.utility.white}
                icon={data?.icon}
                fillColor={data?.icon !== IconTypes.flexi ? mainTheme.colors.primary2 : null}>
                {formatMessage({id: data.label})}
              </FilterChip>
            ))}
          </Flex>
        )}
        <div>
          <RowInfo padding={mainTheme.spacing.oneAndHalf}>
            <Body>
              <FormattedMessage id="admin.billing.currentPlan.startDate" />
            </Body>
            {currentPlan?.startDate && <H4>{moment(currentPlan.startDate).format("DD MMM YYYY")}</H4>}
          </RowInfo>
          {renewalStatus !== RenewalQuoteStatus.CancellationCreated && (
            <RowInfo padding={mainTheme.spacing.oneAndHalf}>
              <Body>
                <FormattedMessage id="admin.billing.currentPlan.renewalDate" />
              </Body>
              {currentPlan?.endDate && <H4>{moment(currentPlan.endDate).format("DD MMM YYYY")}</H4>}
            </RowInfo>
          )}
        </div>
        <Flex gap={mainTheme.spacing.oneAndHalf} marginTop={4}>
          <Button onClick={onChangeModal}>
            <FormattedMessage id="admin.billing.currentPlan.renewal.changePlanBtn" />
          </Button>
          {isRenewalWindowOpen && isRenewalAvailable && (
            <Button primary onClick={() => onCallback(RenewalPlanSubSource.currentPlanCTA)}>
              <FormattedMessage id="admin.billing.currentPlan.renewal.btn" />
            </Button>
          )}
        </Flex>
      </Card>
      {isRenewalCancelled && (
        <>
          <CustomCard bgColor={mainTheme.colors.primary2} marginTop={4} padding={3}>
            <RenewalHeader
              title="admin.billing.currentPlan.cancellation.header.title"
              body="admin.billing.currentPlan.cancellation.header.body"
              pending="admin.billing.currentPlan.cancellation.header.pending"
              showPending
              addOns={addOns}
            />
          </CustomCard>
          <Card bgColor={mainTheme.colors.greys.lightest} padding={3} noBorder>
            <Flex alignItems="center" justifyContent="center" direction="column">
              <H2 marginBottom>
                <FormattedMessage id="admin.billing.currentPlan.cancellation.header.subTitle" />
              </H2>
              <Body color={mainTheme.colors.filterChip}>
                <FormattedMessage id="admin.billing.currentPlan.cancellation.header.body2" />
              </Body>
            </Flex>
          </Card>
        </>
      )}
    </>
  )
}

export default injectIntl(CurrentPlanInfo)
